import React, { useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { IStore } from "redux/store";
import { Dispatch } from "redux";
import classNames from "classnames";
import { Toaster } from "react-hot-toast";
import toolActions from "redux/app/tool/actions";
import toolSelectors from "redux/app/tool/selectors";
import JobsHistory from "./JobsHistory";
import PdfCron from "./PdfCron";
import FailureCron from "./FailureCron";
import BullMQQueue from "./BullMQQueue";

type TabType = "logs" | "pdfCron" | "failureCron" | "bullmqQueue";

interface Tab {
	id: TabType;
	name: string;
}

const tabs: Tab[] = [
	{
		id: "logs",
		name: "Logs History",
	},
	{
		id: "pdfCron",
		name: "PDF Cron",
	},
	{
		id: "failureCron",
		name: "Failure Cron",
	},
	{
		id: "bullmqQueue",
		name: "Queue",
	},
];

type OwnProps = {
	onBack: () => void;
};

type LogsManagementTabsProps = PropsFromRedux & OwnProps;

function LogsManagementTabs(props: LogsManagementTabsProps) {
	const [activeTab, setActiveTab] = useState<TabType>("logs");
	const [isRefreshing, setIsRefreshing] = useState(false);
	const jobsHistoryRefreshRef = React.useRef<(() => void) | null>(null);
	const bullmqQueueRefreshRef = React.useRef<(() => void) | null>(null);

	const handleRefresh = async () => {
		try {
			setIsRefreshing(true);
			switch (activeTab) {
				case "pdfCron":
					// Use current pagination state or defaults
					props.loadPdfCronData(
						props.pdfCronPagination?.page || 1,
						props.pdfCronPagination?.limit || 10
					);
					break;
				case "failureCron":
					// Use current pagination state or defaults
					props.loadFailureCronData(
						props.failureCronPagination?.page || 1,
						props.failureCronPagination?.limit || 10
					);
					break;
				case "bullmqQueue":
					if (bullmqQueueRefreshRef.current) {
						bullmqQueueRefreshRef.current();
					}
					break;
				case "logs":
					if (jobsHistoryRefreshRef.current) {
						jobsHistoryRefreshRef.current();
					} else {
						props.loadJobsHistory();
					}
					break;
				default:
					break;
			}
		} catch (error) {
			console.error("Error refreshing data:", error);
		} finally {
			setIsRefreshing(false);
		}
	};



	const renderTabContent = () => {
		switch (activeTab) {
			case "logs":
				return <JobsHistory onBack={props.onBack} onRefreshRef={jobsHistoryRefreshRef} />;
			case "pdfCron":
				return <PdfCron />;
			case "failureCron":
				return <FailureCron />;
			case "bullmqQueue":
				return <BullMQQueue onRefreshRef={bullmqQueueRefreshRef} />;
			default:
				return <JobsHistory onBack={props.onBack} onRefreshRef={jobsHistoryRefreshRef} />;
		}
	};

	return (
		<div className="bg-white h-[calc(100vh-98px)] m-2 rounded-md overflow-y-auto p-6">
			<div className="flex justify-between gap-3 flex-col items-center mb-8">
				<div className="flex w-full justify-between items-center">
					<div className="flex items-center gap-4">
						<button
							onClick={props.onBack}
							className="text-gray-600 hover:text-gray-900 transition-colors"
						>
							<svg
								className="w-6 h-6"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M10 19l-7-7m0 0l7-7m-7 7h18"
								/>
							</svg>
						</button>
						<h2 className="text-2xl font-semibold">Jobs Management</h2>
					</div>
					<div className="flex gap-3">
						<button
							onClick={handleRefresh}
							disabled={isRefreshing}
							className={classNames(
								"flex items-center px-4 py-2 text-sm font-medium rounded-md",
								"text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400",
								"transition-colors duration-200"
							)}
						>
							<svg
								className={classNames("mr-2 h-4 w-4", {
									"animate-spin": isRefreshing,
								})}
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
								/>
							</svg>
							{isRefreshing ? "Refreshing..." : "Refresh"}
						</button>
					</div>
				</div>

				<div className="bg-white flex gap-6 flex-col w-full">
					<div className="border-b border-gray-200">
						<nav className="-mb-px flex">
							{tabs.map((tab) => {
								const isActive = activeTab === tab.id;
								return (
									<button
										key={tab.id}
										className={classNames(
											"py-4 px-6 text-center border-b-2 font-medium text-sm",
											isActive
												? "border-blue-500 text-blue-600"
												: "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
										)}
										onClick={() => setActiveTab(tab.id)}
									>
										{tab.name}
									</button>
								);
							})}
						</nav>
					</div>

					<div>{renderTabContent()}</div>
				</div>
			</div>

			<Toaster
				position="top-right"
				toastOptions={{
					duration: 3000,
					style: {
						background: "#333",
						color: "#fff",
					},
				}}
			/>


		</div>
	);
}

function mapStateToProps(store: IStore) {
	const toolStore = (store as any).app.tool;

	return {
		jobsHistoryIsLoading:
			toolSelectors.jobToRefreshLogsPdfJobHistory(toolStore)?.isLoading,
		pdfCronPagination: toolSelectors.getPdfCronPagination(toolStore),
		failureCronPagination: toolSelectors.getFailureCronPagination(toolStore),
	};
}

function mapDispatchToProps(dispatch: Dispatch) {
	return {
		loadPdfCronData: (page: number = 1, limit: number = 10) => 
			dispatch(toolActions.commands.toolPdfCronLoad(page, limit)),
		loadFailureCronData: (page: number = 1, limit: number = 10) =>
			dispatch(toolActions.commands.toolFailureCronLoad(page, limit)),
		loadJobsHistory: () => dispatch(toolActions.commands.toolJobsHistoryLoad()),
	};
}

const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(LogsManagementTabs);
